/**
 * Iter XX.82 — Context-aware progressive reveal.
 *
 * Replaces the turn-based reveal logic (`onUserTurnAdded`) with
 * server-emitted signals derived from the conversation itself.
 *
 * Founder principle:
 *   Every product surface should appear because the conversation
 *   naturally created the need for it — never because the message
 *   counter ticked over.
 *
 * How it works:
 *   1. `/api/web/chat` now returns `reveals: [stages...]` — the
 *      cumulative revealed set for the caller, computed server-side
 *      from context (memory ingest, video invitation, recurring
 *      theme, coaching value delivered).
 *   2. Chat.jsx calls `applyServerReveals(resp.reveals)` on every
 *      response. Idempotent — already-revealed stages are no-ops.
 *   3. On mount, Chat.jsx calls `hydrateFromServer()` which pulls
 *      `/api/reveals/me` so the cumulative state is restored across
 *      reloads and devices (server is the source of truth; the local
 *      state is a cache).
 *   4. Side-channel events (scorecard confirm) call
 *      `advanceRevealServer(stage)` which fires
 *      `POST /api/reveals/advance` and reflects the new state locally.
 *
 * Sticky forever — once unlocked, always unlocked. No re-hiding.
 *
 * When flag OFF:  useReveal(...) always returns true → all surfaces
 *                  render → identical to Iter XX.80 pre-reveal behaviour.
 *
 * Stages ⇢ surfaces (unchanged from Iter XX.81):
 *   memory · season · video · plus_banner · plus · earn · home_screen
 */
import { useEffect, useState } from "react";
import { useFlag } from "./featureFlags";
import { getToken } from "./nativeTokenStorage";
import { credentialsMode } from "./nativePlatform";

const STORAGE_KEY = "gully_reveal_state_v1";
const CHANGE_EVENT = "gully:reveal:changed";
const BROWSER_ID_KEY = "gully_browser_id";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

function _load() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { stages: {}, updated_at: 0 };
    const parsed = JSON.parse(raw);
    return {
      stages: parsed.stages || {},
      updated_at: Number(parsed.updated_at) || 0,
    };
  } catch {
    return { stages: {}, updated_at: 0 };
  }
}

function _save(state) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      ...state, updated_at: Date.now(),
    }));
  } catch { /* localStorage disabled */ }
}

function _emitChange() {
  try { window.dispatchEvent(new Event(CHANGE_EVENT)); }
  catch { /* SSR / no-op */ }
}

function _browserId() {
  try {
    let bid = localStorage.getItem(BROWSER_ID_KEY);
    if (!bid) {
      bid = `br_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
      localStorage.setItem(BROWSER_ID_KEY, bid);
    }
    return bid;
  } catch {
    return null;
  }
}

function _sessionId() {
  try { return localStorage.getItem("gully_web_session_id") || null; }
  catch { return null; }
}

function _authHeaders() {
  const headers = { "Content-Type": "application/json" };
  try {
    const tok = getToken();
    if (tok) headers.Authorization = `Bearer ${tok}`;
  } catch { /* noop */ }
  const bid = _browserId();
  if (bid) headers["X-Browser-Id"] = bid;
  const sid = _sessionId();
  if (sid) headers["X-Session-Id"] = sid;
  return headers;
}

export function revealState() {
  return _load();
}

/**
 * Merge a list of server-emitted reveal stages into local state.
 * Idempotent; add-only (sticky forever).
 */
export function applyServerReveals(list) {
  if (!Array.isArray(list) || list.length === 0) return;
  const s = _load();
  let changed = false;
  for (const stage of list) {
    if (typeof stage !== "string" || !stage) continue;
    if (!s.stages[stage]) {
      s.stages[stage] = true;
      changed = true;
    }
  }
  if (changed) {
    _save(s);
    _emitChange();
  }
}

/**
 * Local-only advance. Kept for tests + resetReveal. Prefer
 * `advanceRevealServer` for real flows so the server row stays
 * authoritative.
 */
export function advanceReveal(stage) {
  if (!stage) return;
  applyServerReveals([stage]);
}

/**
 * Pull the caller's cumulative revealed set from the server on
 * mount. Populates local state so cross-device / post-refresh
 * unlocks are preserved. Never throws.
 */
export async function hydrateFromServer() {
  if (!API_BASE) return;
  try {
    const res = await fetch(`${API_BASE}/api/reveals/me`, {
      method: "GET",
      headers: _authHeaders(),
      credentials: credentialsMode(),
    });
    if (!res.ok) return;
    const body = await res.json().catch(() => null);
    if (body && Array.isArray(body.revealed)) {
      applyServerReveals(body.revealed);
    }
  } catch { /* offline / CORS / disabled — non-fatal */ }
}

/**
 * Force-advance a stage on the server, then mirror locally. Used
 * by side channels that don't go through /web/chat (scorecard
 * confirm, etc.). Fire-and-forget — local state is mirrored
 * optimistically so the UI updates immediately.
 */
export async function advanceRevealServer(stage) {
  if (!stage) return;
  applyServerReveals([stage]);
  if (!API_BASE) return;
  try {
    await fetch(`${API_BASE}/api/reveals/advance`, {
      method: "POST",
      headers: _authHeaders(),
      credentials: credentialsMode(),
      body: JSON.stringify({
        stage,
        session_id: _sessionId(),
        browser_id: _browserId(),
      }),
    });
  } catch { /* non-fatal — local state already reflects the advance */ }
}

/**
 * Hook used by every gated surface. Returns `true` when the surface
 * should render, `false` when it should stay hidden.
 *
 * @param stage             one of the semantic keys above
 * @param opts.defaultVisible  when true, surface remains visible
 *                             even if the stage hasn't been advanced
 *                             yet. Use for callers where we already
 *                             know the user is activated (returning
 *                             signed-in user, admin, etc.)
 */
export function useReveal(stage, opts = {}) {
  const flagOn = useFlag("progressive_reveal_enabled");
  const [state, setState] = useState(() => _load());

  useEffect(() => {
    const handler = () => setState(_load());
    window.addEventListener(CHANGE_EVENT, handler);
    window.addEventListener("storage", handler); // cross-tab
    return () => {
      window.removeEventListener(CHANGE_EVENT, handler);
      window.removeEventListener("storage", handler);
    };
  }, []);

  // Flag OFF → everything visible (control behaviour).
  if (!flagOn) return true;
  // Explicit override for callers that know better.
  if (opts.defaultVisible) return true;
  return Boolean(state.stages[stage]);
}

/**
 * Iter XX.82 — Scorecard confirmed is a deterministic contextual
 * signal (the player has a match on record). Advances Season and
 * Memory server-side so the reveal persists across reloads.
 */
export function onScorecardConfirmed() {
  advanceRevealServer("season");
  advanceRevealServer("memory");
}

/**
 * For debugging / founder-preview only. Local wipe — does NOT clear
 * the server-side `user_reveal_state` row.
 */
export function resetReveal() {
  try { localStorage.removeItem(STORAGE_KEY); }
  catch { /* noop */ }
  _emitChange();
}
